import {makeAutoObservable} from 'mobx'
import {Lang} from 'yougile-lang'
import {TypeUtil} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {
  OptionsItem,
  OptionsItemCheckbox
} from '@components/common/base/dropdown-button-menu/types'
import {TableUserInfo} from '@components/common/users-table/users-table'
import {byTimeGlobalStore} from '@components/statistics/by-time/by-time-global-store'
import {getColumnTimeInterval} from '@components/sticker/my-stickers/board-stickers/sticker-settings/stopwatch/stopwatch-column-utils'
import {getUsers} from '@components/sticker/my-stickers/board-stickers/sticker-settings/utils/get-users'
import {getTimerValue} from '@components/task-timeline/get-timer-value'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {ChatUtils} from '@extensions/chat-utils'
import {Column} from '@extensions/column'
import {Project} from '@extensions/project'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'

export interface ListItem {
  value: DataObjectId
  label: string
  selectable: boolean
  order?: number
  linkView?: boolean
  projectId?: DataObjectId
  children?: ListItem[]
}

interface ColumnFilterOption extends OptionsItemCheckbox {
  id: DataObjectId
}

interface TasksFilterValues {
  users: Set<DataObjectId>
  notSetUsers: boolean
  location?: DataObjectId[]
  taskCompletion?: string[]
  archived?: string[]
}

interface State {
  projectList: OptionsItem<DataObjectId>[]
  boardList: OptionsItem<DataObjectId>[]
  columnsFilterValue?: DataObjectId[]
  boardTasksIds: DataObjectId[]
  currentBoardId: DataObjectId | undefined
  tasksFilterValues: TasksFilterValues
  showHeaderShadow: boolean
  showButtonToTop: boolean
  setToTop: boolean
  openedTaskId?: DataObjectId
  emptyBoard: boolean
}

const CompletionOptions = [
  {text: Lang.msg.report.completed, key: 'completed'},
  {text: Lang.msg.report.notCompleted, key: 'not_completed'}
]
const ArchivedOptions = [
  {text: Lang.msg.report.archived, key: 'archived'},
  {text: Lang.msg.report.notArchived, key: 'not_archived'}
]

export class ByTimeStore {
  state: State

  constructor() {
    this.state = {
      boardTasksIds: [],
      projectList: [],
      boardList: [],
      showHeaderShadow: false,
      showButtonToTop: false,
      setToTop: false,
      currentBoardId: byTimeGlobalStore.state.selectedBoardId,
      tasksFilterValues: {
        users: new Set(),
        notSetUsers: false
      },
      emptyBoard: false
    }

    this.syncProjectList()

    makeAutoObservable(this, {}, {autoBind: true})
  }

  setOpenedTaskId(): void {
    const openChatHub = ChatUtils.getOpenedChatHub()
    this.state.openedTaskId = openChatHub?.id
  }

  syncProjectList(): void {
    const box = Sync.getCurrentSandbox()
    this.state.projectList = Project.listProjects(
      box.companyId,
      'not-archive',
      true
    )
      .map(project => {
        const boards = Project.listBoards(project)

        return {
          key: project.id,
          text: project.title,
          order: Project.projectOrder(box.companyId, project.id),
          hasBoards: !!boards.length
        }
      })
      .filter(project => project.hasBoards)
      .sort((a, b) => a.order - b.order)
  }

  syncBoardList(): void {
    const projectHub = Sync.getCurrentSandbox().getHub(
      byTimeGlobalStore.state.selectedProjectId
    )

    this.state.boardList = projectHub
      ? Project.listBoards(projectHub).map(board => {
          return {
            key: board.id,
            text: board.title
          }
        })
      : []
  }

  syncBoardTasksIds(): void {
    const board = this.currentBoard

    if (!board) {
      if (this.state.boardTasksIds.length) {
        return
      }
      this.state.boardTasksIds = []
      return
    }
    const box = Sync.get(board)
    const tasksIds: DataObjectId[] = []
    box.walkForward(board, (obj, level) => {
      if (obj.deleted || obj.title.startsWith('.')) {
        return false
      }
      if (level === 2) {
        tasksIds.push(obj.id)
        return false
      }
      return true
    })

    if (!tasksIds.length) {
      this.state.emptyBoard = true
    }

    this.state.boardTasksIds = tasksIds
  }

  setDefaultFilterValues(): void {
    this.state.tasksFilterValues = {
      users: new Set(),
      notSetUsers: false
    }
  }

  onSelectBoard(option: OptionsItem<DataObjectId>): void {
    const boardId = option.key

    if (option.key !== this.state.currentBoardId) {
      this.state.emptyBoard = false
    }
    this.state.currentBoardId = boardId
    byTimeGlobalStore.onSelectBoard(boardId)
  }

  get currentBoard(): Hub | null {
    const boardId = this.state?.currentBoardId
    if (!boardId) {
      return null
    }
    const box = Sync.getCurrentSandbox()
    return box.getHub(boardId) || null
  }

  get currentProject(): Hub | null {
    const projectId = byTimeGlobalStore.state.selectedProjectId
    if (!projectId) {
      return null
    }
    const box = Sync.getCurrentSandbox()
    return Project.find({
      id: projectId,
      companyId: box.companyId
    })
  }

  get boardColumns(): Hub[] {
    const board = this.currentBoard

    if (!board) {
      return []
    }
    let columns = Board.listColumns(board)

    const order = Board.getColumnsOrder(board).order
    columns = columns
      .sort((a, b) => order(a) - order(b))
      .filter(column => {
        return !Column.isMirror(column)
      })

    return columns
  }

  get filteredBoardColumns(): Hub[] {
    return !this.state.columnsFilterValue
      ? this.boardColumns
      : this.boardColumns.filter(column => {
          return this.state.columnsFilterValue?.includes(column.id)
        })
  }

  get boardTasks(): Hub[] {
    const tasks: Hub[] = this.state.boardTasksIds.reduce((acc, taskId) => {
      const task = this.getTaskHubById(taskId)
      return task ? [...acc, task] : acc
    }, [])

    const tasksFilters = this.state.tasksFilterValues

    let resultWithFilters = tasks

    if (
      tasksFilters?.archived?.length &&
      tasksFilters?.archived.length !== ArchivedOptions.length
    ) {
      resultWithFilters = resultWithFilters.filter(task => {
        if (tasksFilters.archived?.includes('archived')) {
          return Task.isArchived(task, this.currentBoard?.companyId)
        }
        if (tasksFilters.archived?.includes('not_archived')) {
          return !Task.isArchived(task, this.currentBoard?.companyId)
        }
        return true
      })
    }

    if (
      tasksFilters?.taskCompletion?.length &&
      tasksFilters?.taskCompletion.length !== CompletionOptions.length
    ) {
      resultWithFilters = resultWithFilters.filter(task => {
        const taskIsDone = this.getTaskIsDone(task.id)
        if (tasksFilters.taskCompletion?.includes('completed')) {
          return taskIsDone
        }
        if (tasksFilters.taskCompletion?.includes('not_completed')) {
          return !taskIsDone
        }
        return true
      })
    }

    if (tasksFilters.notSetUsers || !!tasksFilters.users?.size) {
      resultWithFilters = resultWithFilters.filter(task => {
        const assignedUsers = this.getAssignedUser(task.id)

        if (tasksFilters.notSetUsers && !assignedUsers?.length) {
          return true
        }
        if (assignedUsers?.length && tasksFilters.users?.size) {
          return assignedUsers.some(userId => tasksFilters.users?.has(userId))
        }
        return false
      })
    }

    if (tasksFilters.location?.length) {
      resultWithFilters = resultWithFilters.filter(task => {
        const taskIntervals = getTimerValue(task)
        const taskColumnsIntervals = getColumnTimeInterval(task, taskIntervals)
        return tasksFilters.location?.some(columnId =>
          taskColumnsIntervals.find(
            taskColumn => taskColumn.column?.id === columnId
          )
        )
      })
    }

    return resultWithFilters
  }

  get locationOptions(): ColumnFilterOption[] {
    return this.boardColumns?.map(column => {
      return {
        id: column.id,
        key: column.id,
        text: column.title,
        selected: !this.state.columnsFilterValue
          ? true
          : Boolean(this.state.columnsFilterValue.includes(column.id))
      }
    })
  }

  get columnsFilterValue(): string {
    const selectedColumns = this.locationOptions
      .filter(column => column.selected)
      .map(column => column.text)

    if (!selectedColumns.length) {
      return Lang.msg.component.dropdown.notSelected
    }

    if (selectedColumns.length === this.locationOptions.length) {
      return `${Lang.msg.allColumns} (${selectedColumns.length})`
    }

    if (selectedColumns.length === 1) {
      return selectedColumns[0]
    }

    return Lang.msg.report.columnNumbers(selectedColumns.length)
  }

  get tasksLocationOptions(): ColumnFilterOption[] {
    return this.locationOptions.map(locationOpt => {
      return {
        ...locationOpt,
        selected: !this.state.tasksFilterValues.location
          ? false
          : Boolean(
              this.state.tasksFilterValues.location.includes(locationOpt.id)
            )
      }
    })
  }

  get tasksLocationFilterValue(): string {
    const selectedColumns = this.tasksLocationOptions
      .filter(column => column.selected)
      .map(column => column.text)

    if (selectedColumns.length === this.locationOptions.length) {
      return Lang.msg.all
    }

    if (!selectedColumns.length) {
      return Lang.msg.component.dropdown.notSelected
    }

    if (selectedColumns.length === 1) {
      return selectedColumns[0]
    }

    return Lang.msg.report.columnNumbers(selectedColumns.length)
  }

  get tasksCompletionOptions(): OptionsItemCheckbox[] {
    const taskCompletion = this.state.tasksFilterValues.taskCompletion

    return CompletionOptions.map(item => ({
      ...item,
      selected: !taskCompletion
        ? false
        : Boolean(taskCompletion.includes(item.key))
    }))
  }

  get tasksArchivedOptions(): OptionsItemCheckbox[] {
    const taskArchived = this.state.tasksFilterValues.archived

    return ArchivedOptions.map(item => ({
      ...item,
      selected: !taskArchived ? false : Boolean(taskArchived.includes(item.key))
    }))
  }

  get tasksCompletionValue(): string {
    const selectedColumns = this.tasksCompletionOptions
      .filter(column => column.selected)
      .map(column => column.text)

    if (!selectedColumns.length) {
      return Lang.msg.component.dropdown.notSelected
    }

    return selectedColumns.length === this.tasksCompletionOptions.length
      ? Lang.msg.statsTimeline.any
      : selectedColumns.join(` ${Lang.msg.and} `)
  }

  get tasksArchivedValue(): string {
    const selectedColumns = this.tasksArchivedOptions
      .filter(column => column.selected)
      .map(column => column.text)

    if (!selectedColumns.length) {
      return Lang.msg.component.dropdown.notSelected
    }

    return selectedColumns.length === this.tasksArchivedOptions.length
      ? Lang.msg.statsTimeline.any
      : selectedColumns.join(` ${Lang.msg.and} `)
  }

  get currentBoardUsers(): TableUserInfo[] {
    return this.currentBoard ? getUsers(this.currentBoard) : []
  }

  setFilterValue<T extends keyof TasksFilterValues>(
    filterKey: T,
    filterValue: TasksFilterValues[T]
  ): void {
    this.state.tasksFilterValues[filterKey] = filterValue
  }

  toggleUsersFilter(users: DataObjectId[]): void {
    const usersFilter = new Set(this.state.tasksFilterValues.users)

    users.forEach(userId => {
      if (usersFilter.has(userId)) {
        usersFilter.delete(userId)
      } else {
        usersFilter.add(userId)
      }
    })

    this.setFilterValue('users', usersFilter)
  }

  selectAllUsersFilter(): void {
    this.setFilterValue(
      'users',
      new Set(this.currentBoardUsers.map(user => user.userId))
    )
  }

  toggleUsersFilterNotSet(): void {
    this.setFilterValue(
      'notSetUsers',
      !this.state.tasksFilterValues.notSetUsers
    )
  }

  get usersFilterValue(): string {
    const selectedUsersId = Array.from(
      this.state.tasksFilterValues?.users || []
    )
    const notSetUsers = this.state.tasksFilterValues.notSetUsers

    if (!selectedUsersId.length && !notSetUsers) {
      return Lang.msg.component.dropdown.notSelected
    }

    if (!selectedUsersId.length && notSetUsers) {
      return Lang.msg.statsNotSet
    }

    let usersResult = ''

    if (selectedUsersId.length === this.currentBoardUsers.length) {
      usersResult = Lang.msg.component.dropdown.all
    } else if (selectedUsersId.length < 3) {
      usersResult = selectedUsersId
        .map(userId => {
          return (
            this.currentBoardUsers.find(
              boardUser => boardUser.userId === userId
            )?.userName || ''
          )
        })
        .join(', ')
    } else if (selectedUsersId.length > 1) {
      usersResult = `${Lang.msg.component.dropdown.selected} ${selectedUsersId.length} ${Lang.msg.component.dropdown.outOf} ${this.currentBoardUsers.length}`
    }

    if (this.state.tasksFilterValues.notSetUsers) {
      return `${usersResult}, ${Lang.msg.report.includingNotSetUsers}`
    }

    return usersResult
  }

  get filtersSummary(): string {
    let result = ''
    const {tasksFilterValues} = this.state

    result = `${result}${
      tasksFilterValues.users?.size || tasksFilterValues.notSetUsers
        ? ` ${Lang.msg.report.designatedEmployees}: ${this.usersFilterValue};`
        : ''
    }`

    result = `${result}${
      tasksFilterValues.location?.length
        ? ` ${Lang.msg.report.location}: ${this.tasksLocationFilterValue};`
        : ''
    }`

    result = `${result}${
      tasksFilterValues.taskCompletion?.length
        ? ` ${Lang.msg.report.completeness}: ${this.tasksCompletionValue};`
        : ''
    }`

    result = `${result}${
      tasksFilterValues.archived?.length
        ? ` ${Lang.msg.report.archival}: ${this.tasksArchivedValue};`
        : ''
    }`

    return result
  }

  onChangeColumnFilter(selectedValues: ColumnFilterOption[]): void {
    this.state.columnsFilterValue = selectedValues
      .filter(option => option.selected)
      .map(option => option.id)
  }

  resetChangeColumnFilter(): void {
    this.state.columnsFilterValue = this.locationOptions.map(item => item.id)
  }

  onChangeTaskLocationFilter(selectedValues: ColumnFilterOption[]): void {
    this.setFilterValue(
      'location',
      selectedValues.filter(option => option.selected).map(option => option.id)
    )
  }

  onChangeTaskCompletionFilter(selectedValues: OptionsItemCheckbox[]): void {
    this.setFilterValue(
      'taskCompletion',
      selectedValues
        .filter(option => option.selected)
        .map(option => option.key)
        .filter(TypeUtil.isTruthy)
    )
  }

  onChangeTaskArchivedFilter(selectedValues: OptionsItemCheckbox[]): void {
    this.setFilterValue(
      'archived',
      selectedValues
        .filter(option => option.selected)
        .map(option => option.key)
        .filter(TypeUtil.isTruthy)
    )
  }

  setButtonTopVisible(value: boolean): void {
    this.state.showButtonToTop = value
  }

  setToTop(value: boolean): void {
    this.state.setToTop = value
  }

  setHeaderShadow(value: boolean): void {
    if (this.state.showHeaderShadow !== value) {
      this.state.showHeaderShadow = value
    }
  }

  getTaskHubById(taskId: DataObjectId): Hub | undefined {
    return Task.getById(taskId)
  }

  getTaskIsDone(taskId: DataObjectId): boolean {
    const task = this.getTaskHubById(taskId)
    return task?.data?.gtd?.state?.stage === 'done'
  }

  getTaskIsArchived(taskId: DataObjectId): boolean | undefined {
    const task = this.getTaskHubById(taskId)
    return task
      ? Task.isArchived(task, this.currentBoard?.companyId)
      : undefined
  }

  getAssignedUser(taskId: DataObjectId): null | DataObjectId[] {
    const task = this.getTaskHubById(taskId)

    if (task) {
      return Sticker.getUsers(task)
    }

    return null
  }
}

export const [ByTimeContext, useByTimeStore] = createUseStore(ByTimeStore)
